<template>
    <kiosk-layout>
        <div>
            <page-header>Reassigned Numbers</page-header>

            <div class="flex flex-col gap-8">
                <kiosk-rnd-balance :available="props.rndAvailable"></kiosk-rnd-balance>

                <kiosk-rnd-schedules :refreshKey="runsRefreshKey"></kiosk-rnd-schedules>

                <div class="max-w-lg" data-test="team-selectable">
                    <vx-label size="md">Team</vx-label>
                    <vx-selectable
                        v-model="teamId"
                        placeholder="Search by team ID or name..."
                        :reduce="(option: TeamOption) => option.value"
                        :loading="loadingTeams"
                        :options="teamOptions ?? []"
                        :clearable="false"
                        @search="searchTeams"
                    ></vx-selectable>
                </div>

                <kiosk-rnd-team-panel
                    v-if="teamId"
                    :key="teamId"
                    :teamId="teamId"
                    :refreshKey="runsRefreshKey"
                    @changed="runsChanged"
                ></kiosk-rnd-team-panel>

                <empty-state v-else data-test="no-team"
                    >Select a team to configure reassigned number checks.</empty-state
                >

                <kiosk-rnd-history :refreshKey="runsRefreshKey" @changed="runsChanged"></kiosk-rnd-history>
            </div>
        </div>
    </kiosk-layout>
</template>

<script lang="ts" setup>
import { VxLabel, VxSelectable } from '@voxie/frontend-components';
import { onUnmounted, ref, watch } from 'vue';
import EmptyState from '~/components/general/EmptyState.vue';
import PageHeader from '~/components/general/PageHeader.vue';
import KioskLayout from '~/components/kiosk/KioskLayout.vue';
import KioskRndBalance from '~/components/kiosk/kiosk-reassigned-numbers/KioskRndBalance.vue';
import KioskRndTeamPanel from '~/components/kiosk/kiosk-reassigned-numbers/KioskRndTeamPanel.vue';
import KioskRndHistory from '~/components/kiosk/kiosk-reassigned-numbers/runs/KioskRndHistory.vue';
import KioskRndSchedules from '~/components/kiosk/kiosk-reassigned-numbers/schedules/KioskRndSchedules.vue';
import { useSearchRequest } from '~/composables/useSearchRequest';
import { useToasted } from '~/composables/useToasted';
import { adminTeamsClient } from '~/services';

interface TeamOption {
    label: string;
    value: number;
}

const props = withDefaults(defineProps<{ rndAvailable?: boolean }>(), { rndAvailable: false });

const toasted = useToasted();

const teamId = ref<number>();
const runsRefreshKey = ref(0);

const runsChanged = () => {
    runsRefreshKey.value += 1;
};

const {
    loading: loadingTeams,
    response: teamOptions,
    error: teamSearchError,
    createSearch,
    cancelSearch,
    cancelKey,
} = useSearchRequest<TeamOption[]>(adminTeamsClient, 'kiosk-rnd-teams');

const searchTeams = createSearch(async (query?: string) => {
    const { data } = await adminTeamsClient.getTeams({ query }, { cancelKey });

    return data.data.map((team) => ({
        value: team.id,
        label: `${team.id} - ${team.name}`.trim(),
    }));
});

watch(teamSearchError, (message) => {
    if (message !== undefined) {
        toasted.error(message);
    }
});

searchTeams();

onUnmounted(() => {
    cancelSearch();
});
</script>
